import React, { useState, useEffect, useCallback } from 'react';
import Sidebar from './components/Sidebar';
import SourceBar from './components/SourceBar';
import { ToastContainer, showToast, BackButton } from './components/UI';
import { api } from './api';
import LoginPage from './pages/LoginPage';
import { useTheme } from './hooks/useTheme';
// Pages
import Overview from './pages/Overview';
import AuditIssues from './pages/AuditIssues';
import Statutory from './pages/Statutory';
import Receivables from './pages/Receivables';
import Payables from './pages/Payables';
import TrialBalance from './pages/TrialBalance';
import SyncHistory from './pages/SyncHistory';
import AuditReport from './pages/AuditReport';
import Configuration from './pages/Configuration';
import Profile from './pages/Profile';
import CAUpgradeQueue from './pages/CAUpgradeQueue';
import Team from './pages/Team';
import Customers from './pages/Customers';
import { SalesRegister, PurchaseRegister, GSTInvoices, BankBook } from './pages/Registers';
import { UnbookedPayments, BalanceCheck } from './pages/Compliance';
import CADashboard from './pages/CADashboard';
import CustomerDashboard from './pages/CustomerDashboard';
import AuditRequestDetail from './pages/AuditRequestDetail';
import EngagementsDashboard from './pages/EngagementsDashboard';
import EngagementDetail from './pages/EngagementDetail';
import Subscription from './pages/Subscription';
import PricingAdmin from './pages/PricingAdmin';
import AgentPair from './pages/AgentPair';
import DevicesSection from './components/DevicesSection';
import EmpanelledCAs from './pages/EmpanelledCAs';
import EngagementRequests from './pages/EngagementRequests';
import CAReviews from './pages/CAReviews';
import UsersAdmin from './pages/UsersAdmin';

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:3000';

function getAuthHeaders() {
  try {
    const saved = localStorage.getItem("auditpros_user");
    if (saved) {
      const parsed = JSON.parse(saved);
      const account = parsed?.account;
      const email = account?.idTokenClaims?.email
        || account?.idTokenClaims?.preferred_username
        || account?.idTokenClaims?.emails?.[0]
        || account?.username || account?.preferred_username || account?.email;
      const viewMode = localStorage.getItem("auditpros_view_mode") || "ca";
      const headers = {};
      if (email) headers["x-user-email"] = email;
      headers["x-view-mode"] = viewMode;
      return headers;
    }
  } catch { /* ignore */ }
  return {};
}

const AUDIT_PAGES = {
  overview: Overview,
  report: AuditReport,
  audit: AuditIssues,
  statutory: Statutory,
  receivables: Receivables,
  payables: Payables,
  trial: TrialBalance,
  sales: SalesRegister,
  purchase: PurchaseRegister,
  gst: GSTInvoices,
  bank: BankBook,
  unbooked: UnbookedPayments,
  balance: BalanceCheck,
  sync: SyncHistory,
};

// ── M7: RegisterSource and TallyConnect removed — replaced by the sync agent.
// If any external code still references those page IDs they'll silently do
// nothing (SYSTEM_PAGES[id] lookup returns undefined). That's fine.
const SYSTEM_PAGES = {
  config: Configuration,
  profile: Profile,
  caqueue: CAUpgradeQueue,
  team: Team,
  customers: Customers,
  subscription: Subscription,
  pricing_admin: PricingAdmin,
  devices: DevicesSection,
  empanelled_cas: EmpanelledCAs,
  engagement_requests: EngagementRequests,
  ca_reviews: CAReviews,
  users_admin: UsersAdmin,
};

export default function App() {
  // ── M3.1: Agent pairing page — full-page overlay, bypasses normal routing ──
  if (typeof window !== 'undefined' && window.location.pathname === '/agent-pair') {
    return <AgentPair />;
  }

  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [userRole, setUserRole] = useState(null);
  const [viewMode, setViewMode] = useState(() => localStorage.getItem("auditpros_view_mode") || "ca");

  // ── Navigation state ──────────────────────────────────────────────────────
  const [view, setView] = useState('dashboard');
  const [dashboardView, setDashboardView] = useState('default'); // 'default' | 'my_requests'
  const [auditPage, setAuditPage] = useState('overview');
  const [activeRequest, setActiveRequest] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [systemPage, setSystemPage] = useState(null);
  // Engagement navigation state
  const [engagementId,     setEngagementId]     = useState('');
  const [activeEngagement, setActiveEngagement] = useState(null);

  // ── Source + sync state ───────────────────────────────────────────────────
  const [sources, setSources] = useState([]);
  const [sourceId, setSourceId] = useState('');
  const [syncRunId, setSyncRunId] = useState('');  // ← specific sync being viewed
  const [customerVisiblePages, setCustomerVisiblePages] = useState(null); // ← pages visible to customer
  const [refreshKey, setRefreshKey] = useState(0);
  const [lastSync, setLastSync] = useState(null);

  // ── Unified audit status — fetched from audit_comments, shown in header ──
  const [auditStatus, setAuditStatus] = useState('synced');
  const [auditStatusSaving, setAuditStatusSaving] = useState(false);

  const { theme, toggleTheme } = useTheme();

  // ── GA4 virtual page-view on SPA navigation ───────────────────────────────
  useEffect(() => {
    if (typeof window.gtag !== 'function') return;
    const page = systemPage || (view === 'audit' ? auditPage : view);
    window.gtag('event', 'page_view', {
      page_title: `AuditPrOS – ${page}`,
      page_location: window.location.href,
      page_path: `/${page}`,
    });
  }, [view, auditPage, systemPage]);

  // Fetch the unified audit status — ONLY when actually viewing a specific audit sync.
  useEffect(() => {
    if (view !== 'audit' || !syncRunId) {
      setAuditStatus('synced');
      return;
    }

    const controller = new AbortController();

    fetch(`${BASE}/audit-comments/status?syncRunId=${syncRunId}`, {
      headers: getAuthHeaders(),
      signal: controller.signal,
    })
      .then(r => r.json())
      .then(d => setAuditStatus(d?.status || 'synced'))
      .catch(err => {
        if (err.name !== 'AbortError') setAuditStatus('synced');
      });

    return () => controller.abort();
  }, [syncRunId, view]);

  // Save audit status change — called by SourceBar's inline picker
  const handleAuditStatusChange = useCallback(async (newStatus) => {
    if (!syncRunId) return;
    setAuditStatusSaving(true);
    try {
      const res = await fetch(`${BASE}/audit-comments/status`, {
        method: 'PATCH',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sourceId, syncRunId, status: newStatus,
          comment: `Status set to "${newStatus}" from header`,
        }),
      });
      const data = await res.json();
      if (res.ok) {
        setAuditStatus(data?.status || newStatus);
        showToast('Audit status updated', 'green');
      }
    } catch { /* ignore */ }
    finally { setAuditStatusSaving(false); }
  }, [syncRunId, sourceId]);

  // ── Session check ─────────────────────────────────────────────────────────
  useEffect(() => {
    try {
      const saved = localStorage.getItem("auditpros_user");
      if (saved) { const parsed = JSON.parse(saved); if (parsed?.account) setUser(parsed); }
    } catch { localStorage.removeItem("auditpros_user"); }
    finally { setChecking(false); }
  }, []);

  // ── Fetch user role ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!user) return;
    api.getMe()
      .then(me => {
        setUserRole(me);
        if (!me.isAdmin) { setViewMode("ca"); localStorage.setItem("auditpros_view_mode", "ca"); }
      })
      .catch(() => setUserRole(null));
  }, [user]);

  // ── Load sources (only in audit view) ────────────────────────────────────
  const loadSources = useCallback((selectId) => {
    api.sources()
      .then(data => {
        setSources(data || []);
        if (selectId) setSourceId(selectId);
        else if (data?.length) setSourceId(prev => prev || data[0].id);
      })
      .catch(() => setSources([]));
  }, []);

  useEffect(() => {
    if (user && view === 'audit') loadSources();
  }, [user, viewMode, view]);

  useEffect(() => {
    if (!sourceId) return;
    api.syncHistory(sourceId)
      .then(d => setLastSync(d?.runs?.[0]?.synced_at || null))
      .catch(() => { });
  }, [sourceId, refreshKey]);

  // ── Navigation handlers ───────────────────────────────────────────────────

  const handleOpenRequest = useCallback((request) => {
    console.log('[handleOpenRequest] sync_id:', request.sync_id, 'source_id:', request.source_id);
    if (request.source_id) setSourceId(request.source_id);

    if (request.sync_id) setSyncRunId(request.sync_id);

    if (!request.request_id) {
      setAuditPage('overview');
      setSystemPage(null);
      setView('audit');
    } else {
      setActiveRequest(request);
      setSystemPage(null);
      setView('request_detail');
    }
    window.scrollTo(0, 0);
  }, []);

  const handleOpenAudit = useCallback((request) => {
    if (request.source_id) setSourceId(request.source_id);
    if (request.sync_id) setSyncRunId(request.sync_id);
    setCustomerVisiblePages(request.visible_pages || null);
    setActiveRequest(request);
    setAuditPage('overview');
    setSystemPage(null);
    setView('audit');
    window.scrollTo(0, 0);
  }, []);

  const handleBackToDashboard = useCallback(() => {
    setView('dashboard');
    setActiveRequest(null);
    setActiveEngagement(null);
    setEngagementId('');
    setSystemPage(null);
    setSyncRunId(''); setAuditStatus('synced');
    setCustomerVisiblePages(null);
    window.scrollTo(0, 0);
  }, []);

  const handleOpenEngagement = useCallback((eng) => {
    setEngagementId(eng.id);
    setActiveEngagement(eng);
    setSystemPage(null);
    setView('engagement_detail');
    window.scrollTo(0, 0);
  }, []);

  const handleOpenSyncFromEngagement = useCallback((sync) => {
    if (sync.source_id) setSourceId(sync.source_id);
    if (sync.sync_id)   setSyncRunId(sync.sync_id);
    setAuditPage('overview');
    setSystemPage(null);
    setView('audit');
    window.scrollTo(0, 0);
  }, []);

  const handleNav = useCallback((id) => {
    setSidebarOpen(false);
    window.scrollTo(0, 0);

    if (SYSTEM_PAGES[id]) { setSystemPage(id); return; }

    if (id === 'cadashboard' || id === 'customerdashboard') {
      setSystemPage(null); setView('dashboard'); setActiveRequest(null);
      setActiveEngagement(null); setEngagementId('');
      setSyncRunId(''); setCustomerVisiblePages(null);
      setDashboardView('default');
      return;
    }

    if (id === 'my_requests') {
      setSystemPage(null); setView('dashboard'); setActiveRequest(null);
      setActiveEngagement(null); setEngagementId('');
      setSyncRunId(''); setCustomerVisiblePages(null);
      setDashboardView('my_requests');
      return;
    }

    if (AUDIT_PAGES[id]) {
      if (view === 'audit') { setAuditPage(id); setSystemPage(null); }
      else { setView('dashboard'); setSystemPage(null); }
      return;
    }
  }, [view]);

  // ── Listen for navigation requests from deeply nested components ──────────
  // Detail can be either a string (page id) or an object { page, tab }.
  // When { page, tab } is given, we navigate AND fire a follow-up event the
  // target page can listen for to set its internal tab state.
  useEffect(() => {
    const handler = (e) => {
      const d = e?.detail;
      if (!d) return;
      if (typeof d === 'string') {
        handleNav(d);
      } else if (d.page) {
        handleNav(d.page);
        if (d.tab) {
          // Defer so the target page mounts before receiving the tab event
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent(`audit-pros-${d.page}-tab`, { detail: d.tab }));
          }, 50);
        }
      }
    };
    window.addEventListener('audit-pros-navigate', handler);
    return () => window.removeEventListener('audit-pros-navigate', handler);
  }, [handleNav]);

  const handleRoleUpgrade = useCallback(async (u) => {
    setUserRole(prev => ({ ...prev, ...u }));
    if (u?.subscriptionPlan !== undefined) {
      try {
        const fresh = await api.getMe();
        setUserRole(fresh);
      } catch { /* keep optimistic update on error */ }
    }
  }, []);

  const handleViewModeChange = useCallback((mode) => {
    setViewMode(mode);
    localStorage.setItem("auditpros_view_mode", mode);
    setSourceId(''); setSources([]); setSyncRunId(''); setAuditStatus('synced');
    setRefreshKey(k => k + 1);
    setView('dashboard'); setSystemPage(null); setDashboardView('default');
  }, []);

  const handleRefresh = useCallback(() => { setSyncRunId(''); setRefreshKey(k => k + 1); }, []);
  const handleRegistered = useCallback((s) => { loadSources(s.id); setRefreshKey(k => k + 1); setView('dashboard'); }, [loadSources]);

  const handleAuth = useCallback((info) => {
    setUser(info);
    // M3.1: If mid-pairing when redirected to login, send back to the pair page
    try {
      const pendingCode = sessionStorage.getItem('auditpros_pending_pair_code');
      if (pendingCode) {
        sessionStorage.removeItem('auditpros_pending_pair_code');
        window.location.href = `/agent-pair?code=${encodeURIComponent(pendingCode)}`;
      }
    } catch { /* ignore */ }
  }, []);

  // Capture ?claim=TOKEN from URL on first load — preserve for post-auth processing
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const claimToken = params.get('claim');
      if (claimToken) {
        sessionStorage.setItem('auditpros_pending_claim', claimToken);
        // Clean the token out of the URL without reloading
        const clean = window.location.pathname;
        window.history.replaceState({}, '', clean);
      }
    } catch { /* ignore */ }
  }, []);

  if (checking) return null;
  if (!user) return <LoginPage onAuthenticated={handleAuth} />;

  const isCA = userRole?.isCA || false;

  let pageContent = null;

  if (systemPage) {
    const SystemPageComponent = SYSTEM_PAGES[systemPage];
    if (SystemPageComponent) {
      pageContent = (
        <SystemPageComponent
          key={systemPage}
          sourceId={sourceId}
          onRegistered={handleRegistered}
          userRole={{ ...userRole, viewMode }}
          onRoleUpgrade={handleRoleUpgrade}
          onClose={() => setSystemPage(null)}
          onSwitchAccount={() => { setUser(null); setUserRole(null); setSystemPage(null); }}
          theme={theme}
          onRegisterNew={() => setSystemPage('register')}
        />
      );
    }
  } else if (view === 'engagement_detail' && engagementId) {
    pageContent = (
      <EngagementDetail
        key={engagementId}
        engagementId={engagementId}
        userRole={userRole}
        useCustomerApi={activeEngagement?._isCustomerView === true}
        onBack={() => { setView('dashboard'); setActiveEngagement(null); setEngagementId(''); window.scrollTo(0, 0); }}
        onOpenSync={handleOpenSyncFromEngagement}
      />
    );
  } else if (view === 'audit') {
    const AuditPageComponent = AUDIT_PAGES[auditPage] || Overview;
    pageContent = (
      <>
        {activeEngagement && (
          <BackButton
            onClick={() => { setView('engagement_detail'); setSystemPage(null); }}
            label={`${activeEngagement.engagement_id || activeEngagement.id}${activeEngagement.company_name ? ` — ${activeEngagement.company_name}` : ''}`}
          />
        )}
        {!activeEngagement && activeRequest && (
          <BackButton
            onClick={() => { setView('request_detail'); setSystemPage(null); }}
            label={`${activeRequest.company_name || activeRequest.source_name} request`}
          />
        )}
        <AuditPageComponent
          key={`${auditPage}-${sourceId}-${syncRunId}-${refreshKey}`}
          sourceId={sourceId}
          syncRunId={syncRunId}
          onRegistered={handleRegistered}
          userRole={userRole}
          onRoleUpgrade={handleRoleUpgrade}
        />
      </>
    );
  } else if (view === 'request_detail' && activeRequest) {
    pageContent = (
      <AuditRequestDetail
        requestId={activeRequest.id}
        userRole={userRole}
        onBack={handleBackToDashboard}
        onOpenAudit={handleOpenAudit}
      />
    );
  } else {
    const showCustomerDashboard = !isCA || dashboardView === 'my_requests';
    pageContent = showCustomerDashboard
      ? <CustomerDashboard key={viewMode} userRole={userRole} onOpenSync={handleOpenAudit} onOpenEngagement={handleOpenEngagement} />
      : <EngagementsDashboard key={viewMode} userRole={{ ...userRole, viewMode }} onOpenEngagement={handleOpenEngagement} />;
  }

  const sidebarCurrent = systemPage
    || (view === 'audit' ? auditPage : (
      isCA
        ? (dashboardView === 'my_requests' ? 'my_requests' : 'cadashboard')
        : 'customerdashboard'
    ));

  return (
    <>
      <button onClick={() => setSidebarOpen(true)} className="hamburger-btn">☰</button>
      <ToastContainer />
      <div className="app-layout">
        <Sidebar
          current={sidebarCurrent}
          onNav={handleNav}
          userRole={{ ...userRole, viewMode }}
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          inAuditView={view === 'audit'}
          customerVisiblePages={!isCA ? customerVisiblePages : null}
        />
        <main className="app-main">
          <SourceBar
            sources={view === 'audit' ? sources : []}
            sourceId={view === 'audit' ? sourceId : ''}
            onChange={id => { setSourceId(id); setSyncRunId(''); setRefreshKey(k => k + 1); }}
            onRefresh={view === 'audit' ? handleRefresh : null}
            lastSync={view === 'audit' ? lastSync : null}
            hideSourceSelector={view !== 'audit'}
            user={user} userRole={userRole} viewMode={viewMode}
            onViewModeChange={handleViewModeChange}
            onLogout={() => { setUser(null); setUserRole(null); window.location.href = '/'; }}
            theme={theme}
            onThemeChange={toggleTheme}
            auditStatus={view === 'audit' || view === 'request_detail' ? auditStatus : null}
            auditStatusSaving={auditStatusSaving}
            onAuditStatusChange={handleAuditStatusChange}
            syncRunId={syncRunId}
          />
          {pageContent}
        </main>
      </div>
    </>
  );
}